import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Squelette, Vide } from '../components/ui';
import { useMobile } from '../lib/ecran';
import { dateFr } from '../lib/format';
import { T, langue, reglagesRegionaux } from '../i18n';

const nombre = (n: number) => n.toLocaleString(langue === 'en' ? 'en-GB' : 'fr-FR');

/**
 * Où en est la flotte, sur un entretien donné.
 *
 * « Si on ouvre Vidange, on trouve les différents véhicules avec leur prochaine
 * vidange. » L'écran manquait : il fallait ouvrir les treize fiches une par une
 * pour savoir laquelle allait passer au garage.
 *
 * Le même écran sert au contrôle technique — c'est la même question, posée sur
 * une date plutôt que sur un kilométrage.
 */
type Niveau = 'depasse' | 'proche' | 'ok' | 'jamais';

interface Ligne {
  vehicleId: string;
  immatriculation: string;
  libelle: string;
  km?: number;
  dernierLe?: string | null;
  dernierKm?: number | null;
  prochainKm?: number | null;
  resteKm?: number | null;
  prochaineDate?: string | null;
  dateExpiration?: string | null;
  resteJours?: number | null;
  numero?: string;
  niveau: Niveau;
  texte: string;
}
interface Reponse { libelle?: string; periodiciteKm?: number; aTraiter: number; lignes: Ligne[] }

const STATUT_BADGE: Record<Niveau, string> = { depasse: 'retard', proche: 'retour', jamais: 'option', ok: 'disponible' };
const BADGE = (n: Niveau) => ({ statut: STATUT_BADGE[n], libelle: T.echeances.badge[n] });

export default function Echeances({ mode }: { mode: 'entretien' | 'controle' }) {
  const { cle } = useParams();
  const { peut } = useAuth();
  const mobile = useMobile();
  const [d, setD] = useState<Reponse | null>(null);
  const [erreur, setErreur] = useState('');
  /* Treize lignes tiennent a l'ecran, mais le patron cherche souvent UNE
   * voiture : « ou en est la vidange de la Clio ». */
  const [filtreVehicule, setFiltreVehicule] = useState('');

  const chemin = mode === 'controle' ? '/operations/controle-technique' : `/operations/echeances/${cle}`;

  const charger = useCallback(() => {
    setD(null); setErreur('');
    api.get<Reponse>(chemin).then(setD).catch(() => setErreur(T.echeances.erreurChargement));
  }, [chemin]);
  useEffect(charger, [charger]);

  if (erreur) return <Bandeau type="ko" titre={T.echeances.chargementImpossible}>{erreur}</Bandeau>;
  if (!d) return <Squelette lignes={6} />;

  const te = T.echeances;
  const u = reglagesRegionaux().uniteDistance;
  const titre = mode === 'controle' ? te.controle : (d.libelle ?? te.entretien);
  const visibles = filtreVehicule ? d.lignes.filter((l) => l.vehicleId === filtreVehicule) : d.lignes;
  const parNiveau = (n: Niveau) => d.lignes.filter((l) => l.niveau === n);
  const urgentes = [...parNiveau('depasse'), ...parNiveau('proche')];

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{titre}</h1>
          <p>
            {mode === 'controle'
              ? te.sousControle
              : d.periodiciteKm
                ? te.sousPeriode(nombre(d.periodiciteKm), u)
                : te.sousDefaut}
          </p>
        </div>
        {peut('modifier.vehicule') && mode === 'entretien' && (
          <div className="actions">
            <Link className="btn btn-p" to="/entretien">{te.enregistrerIntervention}</Link>
          </div>
        )}
      </div>

      {urgentes.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <Bandeau type={parNiveau('depasse').length > 0 ? 'ko' : 'warn'}
                   titre={te.aTraiter(urgentes.length)}>
            {parNiveau('depasse').length > 0 && (
              <><b>{te.depassees(parNiveau('depasse').length)}</b> — {parNiveau('depasse').map((l) => l.immatriculation).join(', ')}. </>
            )}
            {parNiveau('proche').length > 0 && (
              <>{te.proches(parNiveau('proche').length, parNiveau('proche').map((l) => l.immatriculation).join(', '))}</>
            )}
          </Bandeau>
        </div>
      ) : (
        <div style={{ marginBottom: 16 }}>
          <Bandeau type="ok" titre={te.rien}>
            {te.aucuneApproche}
            {mode === 'entretien' && te.seuil(u)}.
          </Bandeau>
        </div>
      )}

      <Carte pad={false} action={
        <select value={filtreVehicule} onChange={(e) => setFiltreVehicule(e.target.value)}
                style={{ height: 32, borderRadius: 8, border: '1px solid var(--bord-fort)', padding: '0 8px' }}>
          <option value="">{te.toutesVoitures}</option>
          {d.lignes.map((l) => (
            <option key={l.vehicleId} value={l.vehicleId}>{l.libelle} — {l.immatriculation}</option>
          ))}
        </select>
      } titre={te.parVoiture}>
        {visibles.length === 0 ? (
          <div className="carte-p">
            <Vide titre={d.lignes.length === 0 ? te.aucuneFlotte : te.aucuneFiltre} />
          </div>
        ) : mobile ? (
          <div className="cartes">
            {visibles.map((l) => (
              <Link key={l.vehicleId} className="carte-ligne" to="/flotte">
                <div className="cl-tete">
                  <span className="cl-titre">{l.libelle}</span>
                  <Badge statut={BADGE(l.niveau).statut} libelle={BADGE(l.niveau).libelle} />
                </div>
                <div className="cl-sous">{l.immatriculation}{l.km != null && ` · ${nombre(l.km)} ${u}`}</div>
                <div className="cl-pied" style={{ color: l.niveau === 'depasse' ? 'var(--danger)' : undefined }}>
                  {l.texte}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                {mode === 'controle' ? (
                  <tr>
                    <th>{te.col.vehicule}</th><th>{te.col.numero}</th><th>{te.col.expire}</th>
                    <th>{te.col.echeance}</th><th>{te.col.etat}</th>
                  </tr>
                ) : (
                  <tr>
                    <th>{te.col.vehicule}</th><th className="droite">{te.col.compteur}</th>
                    <th>{te.col.derniere}</th><th className="droite">{te.col.prochaine}</th>
                    <th>{te.col.echeance}</th><th>{te.col.etat}</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {visibles.map((l) => {
                  const rouge = l.niveau === 'depasse';
                  const orange = l.niveau === 'proche';
                  return (
                    <tr key={l.vehicleId}
                        style={{ background: rouge ? 'var(--danger-doux)' : orange ? 'var(--attention-doux)' : undefined }}>
                      <td><b>{l.libelle}</b><div className="mini sourdine">{l.immatriculation}</div></td>
                      {mode === 'controle' ? (
                        <>
                          <td className="mini">{l.numero ?? '—'}</td>
                          <td>{l.dateExpiration ? dateFr(l.dateExpiration) : '—'}</td>
                        </>
                      ) : (
                        <>
                          <td className="droite num">{l.km != null ? nombre(l.km) : '—'}</td>
                          <td className="mini sourdine">
                            {l.dernierLe
                              ? <>{dateFr(l.dernierLe)}<div>{l.dernierKm != null ? nombre(l.dernierKm) : ''} {u}</div></>
                              : '—'}
                          </td>
                          <td className="droite num">{l.prochainKm != null ? nombre(l.prochainKm) : '—'}</td>
                        </>
                      )}
                      <td style={{ fontWeight: rouge || orange ? 600 : 400,
                                   color: rouge ? 'var(--danger)' : orange ? 'var(--attention)' : 'var(--texte2)' }}>
                        {l.texte}
                      </td>
                      <td><Badge statut={BADGE(l.niveau).statut} libelle={BADGE(l.niveau).libelle} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12 }}>
        {mode === 'entretien'
          ? <>{te.noteEntretien1}<b>{te.noteEntretienSeuil(u)}</b>{te.noteEntretien2}</>
          : <>{te.noteControle}</>}
      </p>
    </>
  );
}
